import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type {
  PipelineStep,
  SessionPipeline,
  StepState,
} from '../api/sessionPipeline';

const COLOR: Record<StepState, string> = {
  done: 'success.main',
  active: 'primary.main',
  waiting: 'text.secondary',
  warning: 'warning.main',
  failed: 'error.main',
  skipped: 'text.disabled',
};

function StepIcon({ state }: { state: StepState }) {
  const sx = { fontSize: 15, color: COLOR[state] };
  switch (state) {
    case 'done':
      return <CheckCircleIcon sx={sx} />;
    case 'active':
      return <CircularProgress size={12} thickness={6} sx={{ color: COLOR.active }} />;
    case 'waiting':
      return <ScheduleIcon sx={sx} />;
    case 'warning':
      return <WarningAmberIcon sx={sx} />;
    case 'failed':
      return <ErrorOutlineIcon sx={sx} />;
    default:
      return <RemoveCircleOutlineIcon sx={sx} />;
  }
}

/** The number on a running step: a percentage when a device is reporting
 *  bytes, else N of M. A stalled upload keeps its last percentage. */
function count(step: PipelineStep): string {
  if (step.percent != null && (step.state === 'active' || step.state === 'warning')) {
    return ` ${step.percent}%`;
  }
  return step.done != null && step.total != null && step.state === 'active'
    ? ` ${step.done}/${step.total}`
    : '';
}

/**
 * Where this session's data is: sensor data → video → video prep → scoring →
 * transcript, one icon + label each, the detail on hover. A step that needs
 * someone's attention (warning / failed) also prints its detail underneath —
 * that's the line people most need, and it shouldn't hide behind a hover.
 */
export function SessionPipelineStrip({ pipeline }: { pipeline: SessionPipeline }) {
  const attention = pipeline.steps.filter(
    (s) => s.state === 'failed' || s.state === 'warning',
  );
  return (
    <Box aria-label="Session processing status">
      <Stack
        direction="row"
        spacing={1.75}
        sx={{ flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}
      >
        {pipeline.steps.map((s) => (
          <Tooltip key={s.key} title={s.detail ?? ''} disableHoverListener={!s.detail}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              data-step={s.key}
              data-state={s.state}
            >
              <StepIcon state={s.state} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: s.state === 'skipped' ? 400 : 600,
                  color: s.state === 'skipped' ? 'text.disabled' : 'text.primary',
                }}
              >
                {s.label}
                {count(s)}
              </Typography>
            </Stack>
          </Tooltip>
        ))}
      </Stack>
      {attention.map((s) => (
        <Typography
          key={s.key}
          variant="caption"
          role={s.state === 'failed' ? 'alert' : 'status'}
          sx={{ display: 'block', mt: 0.5, color: COLOR[s.state] }}
        >
          {s.label}: {s.detail}
        </Typography>
      ))}
    </Box>
  );
}

const DOT: Record<Exclude<SessionPipeline['overall'], 'done'>, string> = {
  in_progress: 'primary.main',
  warning: 'warning.main',
  failed: 'error.main',
};

/** The one step to name in a tile's dot tooltip: the first that isn't done. */
function headline(p: SessionPipeline): string {
  const rank: StepState[] = ['failed', 'warning', 'active', 'waiting'];
  for (const state of rank) {
    const s = p.steps.find((x) => x.state === state);
    if (s) return s.detail ? `${s.label}: ${s.detail}` : s.label;
  }
  return '';
}

/**
 * A tile's status dot — only while a session is still on its way in or needs
 * attention. A finished session shows nothing, so the list stays quiet.
 */
export function SessionPipelineDot({ pipeline }: { pipeline: SessionPipeline }) {
  if (pipeline.overall === 'done') return null;
  return (
    <Tooltip title={headline(pipeline)}>
      <Box
        component="span"
        role="img"
        aria-label={`Processing: ${headline(pipeline)}`}
        data-overall={pipeline.overall}
        sx={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: DOT[pipeline.overall],
          flexShrink: 0,
          ...(pipeline.overall === 'in_progress' && {
            animation: 'pipelinePulse 1.6s ease-in-out infinite',
            '@keyframes pipelinePulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
          }),
        }}
      />
    </Tooltip>
  );
}
